import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {Form, FormField, FormSubmitHandler, showNotification} from 'platform/components';
import {Grid, Space} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNotNil, trim} from 'ramda';

import {
  type PatchCarPassConfigRequestBody,
  useGetCarPassConfigQuery,
  usePatchCarPassConfigMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection, SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {
  CarpassSettingsFormValues,
  carpassSettingsFormSchema,
} from './schemas/CarpassSettingsFormSchema';

const title = i18n.t('page.integrations.labels.carpassConfig');

const CARPASS_ALWAYS_ENABLED = true;

export function CarpassSettingsDetail() {
  const navigate = useNavigate();
  const {branchOptions} = useBranches();

  const {id} = useParams();
  const isCreating = id === 'new';
  const branchId = isCreating ? undefined : id;

  const {data, isLoading, isError} = useGetCarPassConfigQuery(
    isNotNil(branchId) ? {branchId} : skipToken
  );
  const [patchCarPassConfig] = usePatchCarPassConfigMutation();

  const handleSubmit: FormSubmitHandler<CarpassSettingsFormValues> = (
    values,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const payload: PatchCarPassConfigRequestBody = {
      credentials: {
        username: values.username,
        password: trim(values.password),
        enteredOrganisation: values.enteredOrganisation,
        enterpriseOrganisation: values.enterpriseOrganisation,
      },
      enabled: CARPASS_ALWAYS_ENABLED,
    };

    const submitAction = patchCarPassConfig({
      branchId: values.branchId,
      patchCarPassConfigRequestBody: payload,
    });

    const successKey = isCreating
      ? 'entity.carpassConfig.notifications.successfullyCreated'
      : 'general.notifications.changesSuccessfullySaved';

    return submitAction
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t(successKey));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.vehicleServicesCarpass);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues: CarpassSettingsFormValues = {
    branchId: branchId ?? '',
    username: data?.credentials?.username ?? '',
    password: '',
    enteredOrganisation: data?.credentials?.enteredOrganisation ?? '',
    enterpriseOrganisation: data?.credentials?.enterpriseOrganisation ?? '',
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs: [
            {
              label: i18n.t('page.integrations.labels.vehicleServices'),
              href: settingsRoutes.vehicleServices,
            },
            {
              label: i18n.t('page.integrations.labels.carpassConfig'),
              href: settingsRoutes.vehicleServicesCarpass,
            },
          ],
        }}
        data-testid={testIds.settings.vehicleServicesCarpassDetail('settingsTemplate')}
        isLoading={isLoading}
        isError={isError}
      >
        <Form<CarpassSettingsFormValues>
          experimentalZodSchema={carpassSettingsFormSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          shouldWatchForUnsavedChanges
        >
          {(control, _formApi, navigateWithoutBlocker) => (
            <>
              <SettingsSection>
                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="choice"
                    name="branchId"
                    options={branchOptions}
                    label={i18n.t('entity.branch.labels.branch')}
                    data-testid={testIds.settings.vehicleServicesCarpassDetail('branchId')}
                    isDisabled={!isCreating}
                    isNotClearable
                    isRequired={isCreating}
                  />
                </Grid>

                <Space vertical={4} />

                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="username"
                    label={i18n.t('entity.user.labels.userName')}
                    data-testid={testIds.settings.vehicleServicesCarpassDetail('username')}
                    isRequired
                  />
                  <FormField
                    control={control}
                    type="password"
                    name="password"
                    label={i18n.t('general.labels.password')}
                    isRequired
                    data-testid={testIds.settings.vehicleServicesCarpassDetail('password')}
                  />
                </Grid>

                <Space vertical={4} />

                <Grid columns={2}>
                  <FormField
                    control={control}
                    type="text"
                    name="enteredOrganisation"
                    label={i18n.t('page.integrations.labels.carpassConfig.enteredOrganisation')}
                    tooltip={i18n.t('page.integrations.carpassConfig.enteredOrganisationTooltip')}
                    data-testid={testIds.settings.vehicleServicesCarpassDetail(
                      'enteredOrganisation'
                    )}
                    isRequired
                  />
                  <FormField
                    control={control}
                    type="text"
                    name="enterpriseOrganisation"
                    label={i18n.t('page.integrations.labels.carpassConfig.vatNumber')}
                    data-testid={testIds.settings.vehicleServicesCarpassDetail('vatNumber')}
                    isRequired
                  />
                </Grid>
              </SettingsSection>

              <SettingsFooter
                actions={[
                  {
                    type: 'button',
                    title: isCreating
                      ? i18n.t('general.actions.discard')
                      : i18n.t('general.actions.discardChanges'),
                    onClick: () => navigateWithoutBlocker(settingsRoutes.vehicleServicesCarpass),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: isCreating
                      ? i18n.t('general.actions.save')
                      : i18n.t('general.actions.saveChanges'),
                  },
                ]}
              />
            </>
          )}
        </Form>
      </SettingsTemplate>
    </>
  );
}
